// src/publish/serialize/html.js — blocks → clean HTML (CMS / ESP paste, .html).
//
// Rules:
//   strong / em     <strong> / <em>
//   list            <ul><li>…</li></ul> or <ol>
//   link            <a href="URL">testo</a>
//   heading         <h1>…<h3> by level
//   paragraph       <p>; br → <br>
//   cite "[N]"      <sup>[N]</sup>
//   qa              one <p>: <strong>question</strong><br>answer when the answer
//                   is a single paragraph (the FAQ shape the samples paste into a
//                   CMS); a longer answer follows the question as its own blocks
//   slide           <h3>Slide N · Tipo</h3>, <p><strong>headline</strong></p>, body,
//                   <p><em>Indicazione visiva:</em> …</p>
//   source piece    <pre> with the escaped source (piecesToHtml only)
// Every character of text and every attribute is escaped (&, <, >, "); no
// Markdown ever appears here. blocksToHtml gives a fragment; piecesToHtml a
// standalone document whose stylesheet uses the theme tokens.

import i18n from '../../i18n/index.js';
import { COLORS, FONT_TEXT, FONT_MONO } from '../../constants/theme.js';
import { asRuns, asBlocks } from '../blocks.js';
import { slideTitle, listItems } from './plain.js';
import { sourceText } from './markdown.js';

/** Escapes &, <, > and " — safe for text nodes and double-quoted attributes. */
export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** The HTML of a run list (inline only). */
export function runsToHtml(runs) {
  let s = '';
  for (const r of asRuns(runs)) {
    switch (r.type) {
      case 'text': s += escapeHtml(r.text); break;
      case 'br': s += '<br>'; break;
      case 'cite': s += `<sup>[${Number(r.n)}]</sup>`; break;
      case 'strong': { const t = runsToHtml(r.runs); if (t) s += `<strong>${t}</strong>`; break; }
      case 'em': { const t = runsToHtml(r.runs); if (t) s += `<em>${t}</em>`; break; }
      case 'link': {
        const label = runsToHtml(r.runs) || escapeHtml(r.href);
        s += r.href ? `<a href="${escapeHtml(r.href)}">${label}</a>` : label;
        break;
      }
      default: break;
    }
  }
  return s;
}

function listToHtml(block) {
  const tag = block.ordered ? 'ol' : 'ul';
  return `<${tag}>${listItems(block).map((it) => `<li>${runsToHtml(it)}</li>`).join('')}</${tag}>`;
}

function slideToHtml(block) {
  const parts = [`<h3>${escapeHtml(slideTitle(block))}</h3>`];
  const headline = runsToHtml(block.headline);
  if (headline) parts.push(`<p><strong>${headline}</strong></p>`);
  const body = blocksToHtml(block.body);
  if (body) parts.push(body);
  if (block.visualCue) {
    parts.push(`<p><em>${escapeHtml(i18n.t('outputs:pieces.visualCue'))}:</em> ${escapeHtml(block.visualCue)}</p>`);
  }
  return parts.join('\n');
}

/** A qa block: question and a one-paragraph answer share a <p>; a longer answer follows. */
function qaToHtml(block) {
  const q = runsToHtml(block.question);
  const answer = asBlocks(block.answer);
  if (answer.length === 1 && answer[0].type === 'paragraph') {
    const a = runsToHtml(answer[0].runs);
    if (q && a) return `<p><strong>${q}</strong><br>${a}</p>`;
  }
  return [q ? `<p><strong>${q}</strong></p>` : '', blocksToHtml(answer)].filter(Boolean).join('\n');
}

/** Blocks → an HTML fragment, one block per line. */
export function blocksToHtml(blocks) {
  const out = [];
  for (const b of asBlocks(blocks)) {
    switch (b.type) {
      case 'heading': out.push(`<h${b.level}>${runsToHtml(b.runs)}</h${b.level}>`); break;
      case 'paragraph': out.push(`<p>${runsToHtml(b.runs)}</p>`); break;
      case 'list': out.push(listToHtml(b)); break;
      case 'qa': out.push(qaToHtml(b)); break;
      case 'slide': out.push(slideToHtml(b)); break;
      default: break;
    }
  }
  return out.filter((s) => s !== '').join('\n');
}

/**
 * One piece as a fragment: <h2>label</h2>, the destination line when there is
 * one, then its blocks (a 'source' piece as <pre>).
 */
export function pieceToHtmlFragment(piece) {
  if (!piece) return '';
  const parts = [`<h2>${escapeHtml(piece.label)}</h2>`];
  if (piece.destination) parts.push(`<p class="dest">${escapeHtml(piece.destination)}</p>`);
  parts.push(piece.kind === 'source' ? `<pre>${escapeHtml(sourceText(piece))}</pre>` : blocksToHtml(piece.blocks));
  return parts.filter(Boolean).join('\n');
}

const STYLE = [
  `body{font-family:${FONT_TEXT};color:${COLORS.ink};background:${COLORS.surface};line-height:1.6;max-width:760px;margin:0 auto;padding:32px 24px}`,
  'h1{font-size:24px;margin:0 0 12px}h2{font-size:18px;margin:24px 0 8px}h3{font-size:15px;margin:18px 0 6px}',
  `p{margin:8px 0}ul,ol{margin:8px 0 8px 22px}a{color:${COLORS.accent}}`,
  `pre{font-family:${FONT_MONO};font-size:12px;white-space:pre-wrap;background:${COLORS.field};border:1px solid ${COLORS.border};padding:12px}`,
  `.piece{margin:0 0 28px}.piece .dest{color:${COLORS.textSecondary};font-size:12px;margin:-4px 0 8px}`,
].join('\n');

/** Wraps a body fragment in a standalone document (title escaped, theme stylesheet). */
export function htmlDocument({ title = '', body = '', lang } = {}) {
  const language = lang || (i18n.language || 'it').slice(0, 2);
  return `<!doctype html>\n<html lang="${escapeHtml(language)}">\n<head>\n<meta charset="utf-8">\n`
    + `<title>${escapeHtml(title)}</title>\n<style>\n${STYLE}\n</style>\n</head>\n<body>\n${body}\n</body>\n</html>\n`;
}

/**
 * A whole piece list as one standalone HTML document: <h1>title</h1>, then a
 * <div class="piece"> per piece holding pieceToHtmlFragment(piece).
 */
export function piecesToHtml(pieces, { title = '', lang } = {}) {
  const list = Array.isArray(pieces) ? pieces : [];
  const parts = [];
  if (title) parts.push(`<h1>${escapeHtml(title)}</h1>`);
  for (const p of list) {
    if (!p) continue;
    parts.push(`<div class="piece">\n${pieceToHtmlFragment(p)}\n</div>`);
  }
  return htmlDocument({ title, body: parts.join('\n'), lang });
}
